import { useTranslation } from '@hotjar/translations';
import { PageHeader, tokens, Button } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useCurrentSite } from '@hotjar/site';
import {
  findOptionByValue,
  ALL_PAGES_OPTIONS,
} from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/SearchBar/TargetOptionsSelector';
import { getTargetingOptionByTargetingValue } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/SearchBar/SearchBar';
import { PinButton } from '@hotjar/webapp/features/Heatmaps/components/PinButton/PinButton';
import type { GlobalFilter } from '@hotjar/global-filters';
import { EVENT_NAMES } from '@hotjar/heatmaps';
import { itemShared } from '@hotjar/tracking';
import { SharingModule } from '@hotjar/webapp/components/SharingModule';

const StyledPageHeader = styled(PageHeader)`
  margin-bottom: ${tokens.spacer.x4};
`;

const OPTIONS = [...ALL_PAGES_OPTIONS];

export interface HeatmapHeaderProps {
  title: string;
  isPinButtonDisabled: boolean;
  shouldHidePinButton: boolean;
  isCurrentPagePinned: boolean;
  searchPattern: string;
  matchOperation: TargetingValue;
  filters: GlobalFilter;
  isReportLoaded: boolean;
}

export const HeatmapHeader = ({
  title,
  isPinButtonDisabled,
  shouldHidePinButton,
  isCurrentPagePinned,
  searchPattern,
  matchOperation,
  filters,
  isReportLoaded,
}: HeatmapHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();

  const getSubtitle = () => {
    if (isCurrentPagePinned) {
      const matchOperationLabel = findOptionByValue(
        OPTIONS,
        getTargetingOptionByTargetingValue(matchOperation),
      )?.label;

      if (matchOperationLabel) return <>{`${t(matchOperationLabel)}: ${searchPattern}`}</>;
    }

    return null;
  };

  const onCopyLink = () => {
    if (currentSite) {
      itemShared({ item: EVENT_NAMES.SHARE_LINK }, currentSite);
    }
  };

  return (
    <StyledPageHeader
      title={title}
      subtitle={getSubtitle()}
      actions={[
        isReportLoaded && (
          <SharingModule
            onShare={onCopyLink}
            trigger={<Button kind="secondary">{t('common:actions.share')}</Button>}
            key="heatmaps-share-action"
          />
        ),
        <PinButton
          key="pin-button"
          isDisabled={isPinButtonDisabled}
          isHidden={shouldHidePinButton}
          currentSite={currentSite}
          pattern={searchPattern}
          matchOperation={matchOperation}
          isCurrentPagePinned={isCurrentPagePinned}
          filters={filters}
        />,
      ]}
    />
  );
};
